import { useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CompanyCard from './CompanyCard'
import CompanyMap from './CompanyMap'
import { store } from '../../constants/store'
import { Navbar } from '../../components/Navbar'
import { Footer } from '../../components/Footer'
import { PageContainer } from '../../components/styled/PageContainer'
import { useDebounce } from './useDebounce'
import { useGetLocationByPostCode } from '../../api/store-locator'

const EARTH_RADIUS_KM = 6371
const toRad = (value) => (value * Math.PI) / 180

const getDistanceKm = (from, to) => {
  if (!from || !to) return null
  const latDelta = toRad(to.lat - from.lat)
  const lngDelta = toRad(to.lng - from.lng)
  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(lngDelta / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS_KM * c
}

const DEBOUNCE_DELAY = 500
const MALAYSIAN_POSTCODE_LENGTH = 5
const LIMIT_WORKSHOP_TO_SHOW = 5

export const StoreLocator = () => {
  const [search, setSearch] = useState('')
  const [location, setLocation] = useState(null)
  const [inputError, setInputError] = useState(null)

  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY)

  const query = useMemo(() => {
    const trimmed = debouncedSearch.trim()

    if (trimmed.length !== MALAYSIAN_POSTCODE_LENGTH) {
      setInputError(null)
      setLocation(null)
      return null
    }

    setInputError(null)
    return { postcode: trimmed }
  }, [debouncedSearch])

  const { data, isLoading, isError } = useGetLocationByPostCode(query)

  useMemo(() => {
    if (data && data.lat && data.lng) {
      setLocation({ lat: data.lat, lng: data.lng })
      setInputError(null)
    } else if (isError && search.trim().length === MALAYSIAN_POSTCODE_LENGTH) {
      setInputError('Postcode not found')
      setLocation(null)
    }
  }, [data, isError, search])

  const handleSearchChange = (event) => {
    const numericOnly = event.target.value.replace(/\D/g, '').slice(0, MALAYSIAN_POSTCODE_LENGTH)
    setSearch(numericOnly)
  }

  const sortedCompanies = useMemo(() => {
    if (!location) return store.slice(0, LIMIT_WORKSHOP_TO_SHOW)
    return store
      .map((c) => ({
        ...c,
        distance: getDistanceKm(location, { lat: c.lat, lng: c.lng }),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, LIMIT_WORKSHOP_TO_SHOW)
  }, [location])

  return (
    <>
      <Navbar />
      <PageContainer>
        <Box sx={{ py: 4, px: 4 }}>
          <Typography
            variant='h4'
            component='h1'
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 1,
            }}
          >
            Find Workshops Near You
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
            Enter your postcode to find the nearest workshops
          </Typography>
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder='Enter 5-digit postcode (e.g., 81200)'
              value={search}
              onChange={handleSearchChange}
              error={!!inputError}
              helperText={inputError || `${search.length}/${MALAYSIAN_POSTCODE_LENGTH} digits`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon color='action' />
                  </InputAdornment>
                ),
                endAdornment:
                  isLoading && query !== null ? (
                    <InputAdornment position='end'>
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : location ? (
                    <InputAdornment position='end'>
                      <LocationOnIcon color='success' />
                    </InputAdornment>
                  ) : null,
              }}
            />
          </Box>
          {location && (
            <Alert severity='success' icon={<LocationOnIcon />} sx={{ mb: 3 }}>
              Showing workshops nearest to postcode {search}
            </Alert>
          )}
          <Stack spacing={3}>
            <CompanyMap companies={sortedCompanies} location={location} />
            {sortedCompanies.map((c, idx) => (
              <CompanyCard key={idx} company={c} location={location} isNearest={idx === 0} />
            ))}
          </Stack>
        </Box>
      </PageContainer>
      <Footer />
    </>
  )
}
